import { getApp, getApps, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection as collectionRef,
  collectionGroup as collectionGroupRef,
  doc as docRef,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  onSnapshot as onFirestoreSnapshot,
  query as buildQuery,
  where as whereConstraint,
  orderBy as orderByConstraint,
  limit as limitConstraint,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageReference,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const ModularApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
const ModularAuth = getAuth(ModularApp);
const ModularFirestore = getFirestore(ModularApp);
const ModularStorage = getStorage(ModularApp);
const ModularFunctions = getFunctions(ModularApp);
const AppCheck = null;

const buildCollection = (pathSegments) => collectionRef(ModularFirestore, ...pathSegments);
const buildDoc = (pathSegments) => docRef(ModularFirestore, ...pathSegments);

const withQuery = (collectionPath, constraints = []) => {
  const baseCollection = buildCollection(collectionPath);
  return constraints.length ? buildQuery(baseCollection, ...constraints) : baseCollection;
};

const withQueryFromBase = (baseRef, constraints = []) =>
  constraints.length ? buildQuery(baseRef, ...constraints) : baseRef;

const normalizeDocSnapshot = (snapshot) => ({
  id: snapshot.id,
  ref: snapshot.ref,
  exists: snapshot.exists(),
  data: (...args) => snapshot.data(...args),
  get: (...args) => snapshot.get(...args),
});

const normalizeQuerySnapshot = (snapshot) => {
  const docs = snapshot.docs.map((doc) => normalizeDocSnapshot(doc));
  return {
    docs,
    empty: snapshot.empty,
    size: snapshot.size,
    forEach: (callback, thisArg) => {
      docs.forEach((doc, index) => callback.call(thisArg, doc, index));
    },
  };
};

const wrapSnapshotObserver = (observerOrNext, error, complete, normalize) => {
  if (typeof observerOrNext === "function") {
    return {
      next: (snapshot) => observerOrNext(normalize(snapshot)),
      error,
      complete,
    };
  }

  if (observerOrNext && typeof observerOrNext === "object") {
    return {
      ...observerOrNext,
      next: observerOrNext.next ? (snapshot) => observerOrNext.next(normalize(snapshot)) : undefined,
    };
  }

  return observerOrNext;
};

const createDocCompat = (docPath) => ({
  id: docPath[docPath.length - 1],
  collection: (segment) => createCollectionCompat([...docPath, segment]),
  set: (data, options = {}) =>
    setDoc(buildDoc(docPath), data, options?.merge ? { merge: true } : undefined),
  update: (data) => updateDoc(buildDoc(docPath), data),
  get: async () => normalizeDocSnapshot(await getDoc(buildDoc(docPath))),
  delete: () => deleteDoc(buildDoc(docPath)),
  onSnapshot: (observerOrNext, error, complete) =>
    onFirestoreSnapshot(
      buildDoc(docPath),
      wrapSnapshotObserver(observerOrNext, error, complete, normalizeDocSnapshot)
    ),
});

const createCollectionCompat = (collectionPath, constraints = []) => ({
  doc: (id) => createDocCompat([...collectionPath, id]),
  where: (field, op, value) =>
    createCollectionCompat(collectionPath, [...constraints, whereConstraint(field, op, value)]),
  orderBy: (field, direction) =>
    createCollectionCompat(collectionPath, [...constraints, orderByConstraint(field, direction)]),
  limit: (count) => createCollectionCompat(collectionPath, [...constraints, limitConstraint(count)]),
  add: async (data) => {
    const ref = await addDoc(buildCollection(collectionPath), data);
    return createDocCompat([...collectionPath, ref.id]);
  },
  get: async () => normalizeQuerySnapshot(await getDocs(withQuery(collectionPath, constraints))),
  onSnapshot: (observerOrNext, error, complete) =>
    onFirestoreSnapshot(
      withQuery(collectionPath, constraints),
      wrapSnapshotObserver(observerOrNext, error, complete, normalizeQuerySnapshot)
    ),
});

const createCollectionGroupCompat = (groupId, constraints = []) => {
  const baseGroup = collectionGroupRef(ModularFirestore, groupId);
  return {
    where: (field, op, value) =>
      createCollectionGroupCompat(groupId, [...constraints, whereConstraint(field, op, value)]),
    orderBy: (field, direction) =>
      createCollectionGroupCompat(groupId, [...constraints, orderByConstraint(field, direction)]),
    limit: (count) => createCollectionGroupCompat(groupId, [...constraints, limitConstraint(count)]),
    get: async () => normalizeQuerySnapshot(await getDocs(withQueryFromBase(baseGroup, constraints))),
    onSnapshot: (observerOrNext, error, complete) =>
      onFirestoreSnapshot(
        withQueryFromBase(baseGroup, constraints),
        wrapSnapshotObserver(observerOrNext, error, complete, normalizeQuerySnapshot)
      ),
  };
};

const Firestore = {
  collection: (segment) => createCollectionCompat([segment]),
  collectionGroup: (groupId) => createCollectionGroupCompat(groupId),
};

const createStorageRefCompat = (path) => {
  const ref = storageReference(ModularStorage, path);
  return {
    put: (file, metadata) => uploadBytes(ref, file, metadata),
    getDownloadURL: () => getDownloadURL(ref),
    delete: () => deleteObject(ref),
  };
};

const Storage = {
  ref: (path) => createStorageRefCompat(path),
};

const CloudFunctions = {
  httpsCallable: (name) => httpsCallable(ModularFunctions, name),
};

const FirebaseAuth = {
  auth: () => ModularAuth,
  firestore: () => Firestore,
  storage: () => Storage,
  functions: () => CloudFunctions,
  appCheck: () => AppCheck,
};

export { FirebaseAuth, Firestore, CloudFunctions, Storage, AppCheck, ModularApp, ModularAuth };
